/**
 * URL path utilities for path-based locales (SEO PR2, Phase 6).
 *
 * All three locales carry a URL prefix (`/zh-Hant/...`, `/zh-Hans/...`,
 * `/en/...`). The bare-path form is deprecated; the nginx 301 redirect map
 * in `infra/nginx/aapanel.www.yada.hk.conf` forwards old bare URLs to their
 * `/zh-Hant/` equivalent. See [[yada-pr2-phase-6-locales-2026-10-14]] for
 * the full rationale.
 *
 * The Locale type is defined inline (same as src/utils/siteMeta.ts) rather
 * than imported from src/admin/api to avoid utils depending on admin.
 */

export type Locale = 'zh-Hant' | 'zh-Hans' | 'en'

export const DEFAULT_LOCALE: Locale = 'zh-Hant'
/** Every locale that gets a URL prefix, in canonical order. */
export const PREFIXED_LOCALES: readonly Locale[] = ['zh-Hant', 'zh-Hans', 'en']

/** Strip a leading `/<locale>` segment if present. */
export function stripLocalePrefix(pathname: string): string {
  for (const loc of PREFIXED_LOCALES) {
    if (pathname === `/${loc}`) return '/'
    if (pathname.startsWith(`/${loc}/`)) return pathname.slice(loc.length + 1)
  }
  return pathname
}

/** Detect the locale from a URL pathname. Returns `DEFAULT_LOCALE` for the bare path or any unrecognized prefix. */
export function parseLocaleFromPath(pathname: string): Locale {
  for (const loc of PREFIXED_LOCALES) {
    if (pathname === `/${loc}` || pathname.startsWith(`/${loc}/`)) {
      return loc
    }
  }
  return DEFAULT_LOCALE
}

/**
 * Build a URL pathname for the given locale. Every locale now carries a
 * prefix; the bare-path form is no longer produced by this function.
 * Pass an already-prefixed input and the prefix is replaced (idempotent).
 */
export function buildLocalePath(pathname: string, locale: Locale): string {
  const stripped = stripLocalePrefix(pathname)
  return stripped === '/' ? `/${locale}` : `/${locale}${stripped}`
}
